// theme.js — Priosa storefront behavior

// Money formatting: Liquid's `| money` filter renders using the shop's own
// money_format (e.g. "€120,00"), but toLocaleString(undefined, …) renders
// using the BROWSER'S locale — on an en-US browser that drifts to "€120.00",
// so the drawer visibly disagreed with the page. layout/theme.liquid stamps
// `data-money-format="{{ shop.money_format }}"` on <body>; when that attr is
// present and recognized we format by hand to match it exactly. Read lazily
// (theme.js is deferred, so <body> exists by the time any handler runs) and
// cache after the first successful read.
let moneyFormatCache = null;
function readMoneyFormat() {
  if (moneyFormatCache !== null) return moneyFormatCache;
  if (!document.body) return ""; // too early — don't cache, retry next call
  moneyFormatCache = document.body.dataset.moneyFormat || "";
  return moneyFormatCache;
}

// Some shops wrap money_format in markup (e.g. "<span class=money>{{amount}}</span>").
// Consumers use textContent, so the result must be plain text either way.
function stripTags(str) {
  return str.replace(/<[^>]*>/g, "");
}

// Builds the numeric portion for the four money_format placeholder tokens
// Shopify supports: amount, amount_no_decimals, amount_with_comma_separator,
// amount_no_decimals_with_comma_separator. Plain JS — no Intl needed.
function formatMoneyAmount(cents, token) {
  const decimals = token.indexOf("no_decimals") !== -1 ? 0 : 2;
  const useCommaDecimal = token.indexOf("with_comma_separator") !== -1;
  const fixed = (cents / 100).toFixed(decimals);
  const [intPart, decPart] = fixed.split(".");
  const thousandsSep = useCommaDecimal ? "." : ",";
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
  if (decimals === 0) return groupedInt;
  const decimalSep = useCommaDecimal ? "," : ".";
  return groupedInt + decimalSep + decPart;
}

const MONEY_PLACEHOLDER = /\{\{\s*(amount(?:_no_decimals)?(?:_with_comma_separator)?)\s*\}\}/;

const Money = (cents) => {
  const format = stripTags(readMoneyFormat());
  const match = format.match(MONEY_PLACEHOLDER);
  if (format && match) {
    return format.replace(MONEY_PLACEHOLDER, formatMoneyAmount(cents, match[1]));
  }
  // Missing or unrecognized format — fall back to the previous behavior exactly.
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: window.Shopify?.currency?.active || "USD",
  });
};

async function parseOrThrow(response, fallback) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.description || data?.message || fallback;
    throw new Error(message);
  }
  return data;
}

const Cart = {
  async get() {
    const r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return parseOrThrow(r, "Could not load cart");
  },
  async add(id, quantity = 1, properties = null) {
    const body = { id, quantity };
    if (properties && Object.keys(properties).length) body.properties = properties;
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    return parseOrThrow(r, "Could not add to cart");
  },
  async change(id, quantity) {
    const r = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id, quantity }),
    });
    return parseOrThrow(r, "Could not update cart");
  },
};

function setCartCount(count) {
  document.querySelectorAll("[data-cart-count]").forEach((el) => {
    el.textContent = count;
    el.hidden = count === 0;
  });
}

// Public namespace — assigned before any handler runs so call sites are safe.
window.PriosaTheme = { Money, Cart, setCartCount };

document.addEventListener("DOMContentLoaded", () => {
  Cart.get().then((c) => setCartCount(c.item_count)).catch(() => {});
});

function initStaticChrome() {
  const nav = document.getElementById("nav");
  const menu = document.getElementById("menu");
  const navToggle = document.getElementById("navToggle");
  const menuClose = document.getElementById("menuClose");

  const openMenu = () => {
    if (!menu || !navToggle) return;
    menu.classList.add("is-open");
    menu.setAttribute("aria-hidden", "false");
    navToggle.setAttribute("aria-expanded", "true");
    document.body.style.overflow = "hidden";
  };

  const closeMenu = () => {
    if (!menu || !navToggle) return;
    menu.classList.remove("is-open");
    menu.setAttribute("aria-hidden", "true");
    navToggle.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";
  };

  if (navToggle) navToggle.addEventListener("click", openMenu);
  if (menuClose) menuClose.addEventListener("click", closeMenu);
  if (menu) {
    menu.addEventListener("click", (e) => {
      if (e.target === menu) closeMenu();
    });
    menu.querySelectorAll(".menu__link").forEach((link) => {
      link.addEventListener("click", (e) => {
        if (link.hasAttribute("data-coming-soon")) {
          e.preventDefault();
          const soon = menu.querySelector(".menu__soon");
          if (soon) soon.hidden = false;
          return;
        }
        closeMenu();
      });
    });
  }
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeMenu();
  });

  const clockEl = document.getElementById("menuClock");
  const dateEl = document.getElementById("menuDate");
  const pad = (n) => String(n).padStart(2, "0");
  const tick = () => {
    const d = new Date();
    if (clockEl) clockEl.textContent = pad(d.getHours()) + "." + pad(d.getMinutes());
    if (dateEl) dateEl.textContent = pad(d.getDate()) + "." + pad(d.getMonth() + 1) + "." + d.getFullYear();
  };
  tick();
  window.setInterval(tick, 10000);

  if (nav) {
    // The pill's theme must match the section actually BEHIND the pill, so
    // probe live geometry at the pill's own centre each frame. (The previous
    // IntersectionObserver keyed off the viewport centre — half a screen
    // below the pill — and its batched entries arrive in registration order,
    // not chronological order, so fast scrolls could apply a stale theme.)
    // [data-nav] sections are re-queried each frame, not captured once: the
    // Theme Editor swaps section DOM in place (shopify:section:load), and a
    // captured list would leave the pill probing detached nodes forever.
    let navThemeQueued = false;
    const applyNavTheme = () => {
      navThemeQueued = false;
      const navRect = nav.getBoundingClientRect();
      const probeY = navRect.top + navRect.height / 2;
      let theme = "";
      document.querySelectorAll("[data-nav]").forEach((section) => {
        const rect = section.getBoundingClientRect();
        // Skip the other breakpoint's display:none markup (zero-size rects).
        if (!rect.width || !rect.height) return;
        if (rect.top <= probeY && rect.bottom > probeY) theme = section.getAttribute("data-nav") || "dark";
      });
      if (theme) {
        nav.classList.toggle("is-hidden", theme === "hidden");
        if (theme !== "hidden") nav.setAttribute("data-theme", theme);
      }
    };
    const queueNavTheme = () => {
      if (navThemeQueued) return;
      navThemeQueued = true;
      window.requestAnimationFrame(applyNavTheme);
    };
    applyNavTheme();
    window.addEventListener("scroll", queueNavTheme, { passive: true });
    window.addEventListener("resize", queueNavTheme);
    window.addEventListener("load", queueNavTheme);
  }

  initReveals();

}

// .reveal elements stay opacity:0 until observed into view. Root-scoped and
// re-entrant so the Theme Editor's shopify:section:load handler can re-arm the
// reveals inside a re-rendered section (a fresh fragment has no .is-in and no
// observer — without this it stays invisible until a full page refresh).
function initReveals(root = document) {
  const targets = [...root.querySelectorAll(".reveal")];
  if (root !== document && root.matches && root.matches(".reveal")) targets.push(root);
  if (!("IntersectionObserver" in window)) {
    targets.forEach((el) => el.classList.add("is-in"));
    return;
  }
  const revealObserver = new IntersectionObserver(
    (entries, obs) => entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-in");
        obs.unobserve(entry.target);
      }
    }),
    { threshold: 0.18 }
  );
  targets.forEach((el) => {
    if (!el.classList.contains("is-in")) revealObserver.observe(el);
  });
}

// Root-scoped + re-entrant (data-js-init guards): the Theme Editor re-injects
// section HTML on every setting change, and the shopify:section:load handler
// at the bottom of this file re-runs this for the fresh fragment.
function initMobileV2(root = document) {
  root.querySelectorAll("[data-mobile-slider]").forEach((slider) => {
    if (slider.dataset.jsInit) return;
    slider.dataset.jsInit = "1";
    const track = slider.querySelector(".mobile-slider__track");
    const bars = [...slider.querySelectorAll(".mobile-slider__bars span")];
    if (!track || !bars.length) return;
    const update = () => {
      const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      bars.forEach((bar, i) => bar.classList.toggle("is-active", i === index));
    };
    track.addEventListener("scroll", update, { passive: true });
    update();

    // Desktop has no touch-drag: arrow buttons + keyboard step the slider.
    const slideCount = track.children.length;
    const stepTo = (dir) => {
      const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const next = Math.min(slideCount - 1, Math.max(0, index + dir));
      track.scrollTo({ left: next * track.clientWidth, behavior: "smooth" });
    };
    slider.querySelectorAll("[data-slider-prev]").forEach((btn) =>
      btn.addEventListener("click", () => stepTo(-1))
    );
    slider.querySelectorAll("[data-slider-next]").forEach((btn) =>
      btn.addEventListener("click", () => stepTo(1))
    );
    track.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); stepTo(1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); stepTo(-1); }
    });
  });

  root.querySelectorAll("[data-story-slider]").forEach((track) => {
    if (track.dataset.jsInit) return;
    track.dataset.jsInit = "1";
    const slides = [...track.children].filter((child) => child.matches("article"));
    if (slides.length < 2) return;
    track.addEventListener("click", (event) => {
      const button = event.target.closest("[data-story-next]");
      if (!button || !track.contains(button)) return;
      const current = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const next = (current + 1) % slides.length;
      track.scrollTo({ left: slides[next].offsetLeft - track.offsetLeft, behavior: "smooth" });
    });
  });

  const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const frameSequences = [...root.querySelectorAll("[data-frame-sequence]")].filter((sequence) => {
    if (sequence.dataset.jsInit) return false;
    sequence.dataset.jsInit = "1";
    return true;
  });
  if (frameSequences.length && !reduceMotion) {
    const states = frameSequences.map((sequence) => {
      const img = sequence.querySelector("img");
      const count = Number(sequence.dataset.frameCount || 0);
      const pad = Number(sequence.dataset.framePad || 3);
      const ext = sequence.dataset.frameExt || "jpg";
      const base = sequence.dataset.framePath || "";
      const srcFor = (index) => base + String(index + 1).padStart(pad, "0") + "." + ext;
      const frameStart = Number(sequence.dataset.frameStart || 1) - 1;
      const frameEnd = Number(sequence.dataset.frameEnd || count) - 1;
      const start = Math.min(count - 1, Math.max(0, frameStart));
      const end = Math.min(count - 1, Math.max(0, frameEnd));
      const complete = Math.min(1, Math.max(0.2, Number(sequence.dataset.frameComplete || 1)));
      const frameAt = (progress) => Math.round(start + ((end - start) * progress));
      return { sequence, img, count, srcFor, frameAt, complete, active: start, target: start, preloaded: new Set([start]) };
    }).filter((state) => state.img && state.count > 1);

    let frameTicking = false;
    let sequenceAnimating = false;
    const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
    const preloadFrame = (state, index) => {
      if (index < 0 || index >= state.count || state.preloaded.has(index)) return;
      state.preloaded.add(index);
      const preloaded = new Image();
      preloaded.decoding = "async";
      preloaded.src = state.srcFor(index);
    };
    const preloadAround = (state, index, radius = 32) => {
      for (let offset = -radius; offset <= radius; offset += 1) {
        preloadFrame(state, index + offset);
      }
    };
    const scheduleIdlePreload = (state) => {
      let index = 0;
      const preloadNext = (deadline) => {
        let loaded = 0;
        while (
          index < state.count &&
          loaded < 12 &&
          (!deadline || deadline.timeRemaining() > 4)
        ) {
          preloadFrame(state, index);
          index += 1;
          loaded += 1;
        }
        if (index < state.count) {
          if ("requestIdleCallback" in window) {
            window.requestIdleCallback(preloadNext, { timeout: 1600 });
          } else {
            window.setTimeout(() => preloadNext(), 90);
          }
        }
      };
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(preloadNext, { timeout: 1200 });
      } else {
        window.setTimeout(() => preloadNext(), 350);
      }
    };
    states.forEach((state) => {
      state.img.src = state.srcFor(state.active);
      preloadAround(state, state.active, 20);
      scheduleIdlePreload(state);
    });
    const animateSequences = () => {
      let needsNext = false;
      states.forEach((state) => {
        if (state.active === state.target) return;
        const diff = state.target - state.active;
        const step = clamp(diff, -6, 6);
        state.active += step;
        preloadAround(state, state.active);
        state.img.src = state.srcFor(state.active);
        if (state.active !== state.target) needsNext = true;
      });
      if (needsNext) {
        window.requestAnimationFrame(animateSequences);
      } else {
        sequenceAnimating = false;
      }
    };
    const requestSequenceAnimation = () => {
      if (sequenceAnimating) return;
      sequenceAnimating = true;
      window.requestAnimationFrame(animateSequences);
    };
    const updateFrames = () => {
      frameTicking = false;
      states.forEach((state) => {
        if (!state.sequence.isConnected) return; // editor swapped the section out
        const rect = state.sequence.getBoundingClientRect();
        const scrollable = Math.max(1, rect.height - window.innerHeight);
        const rawProgress = clamp(-rect.top / scrollable, 0, 1);
        const progress = clamp(rawProgress / state.complete, 0, 1);
        state.sequence.style.setProperty("--frame-progress", progress.toFixed(4));
        const next = state.frameAt(progress);
        if (next !== state.target) {
          state.target = next;
          preloadAround(state, next);
          requestSequenceAnimation();
        }
      });
    };
    const requestFrameUpdate = () => {
      if (frameTicking) return;
      frameTicking = true;
      window.requestAnimationFrame(updateFrames);
    };
    window.addEventListener("scroll", requestFrameUpdate, { passive: true });
    window.addEventListener("resize", requestFrameUpdate);
    requestFrameUpdate();
  }

  const videoSequences = [...root.querySelectorAll("[data-video-sequence]")].filter((sequence) => {
    if (sequence.dataset.jsInit) return false;
    sequence.dataset.jsInit = "1";
    return true;
  });
  if (videoSequences.length && !reduceMotion) {
    const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
    const states = videoSequences.map((sequence) => {
      const video = sequence.querySelector("video");
      const complete = Math.min(1, Math.max(0.2, Number(sequence.dataset.videoComplete || 1)));
      const maxStep = Math.min(0.08, Math.max(0.012, Number(sequence.dataset.videoStep || 0.024)));
      const snapStep = Math.min(0.012, maxStep * 0.5);
      const reverse = sequence.dataset.videoReverse === "true";
      return { sequence, video, complete, maxStep, snapStep, reverse, active: 0, target: 0, ready: false };
    }).filter((state) => state.video);

    let videoTicking = false;
    let videoAnimating = false;

    const videoProgress = (state, time = state.active) => {
      const duration = state.video.duration;
      if (!Number.isFinite(duration) || duration <= 0) return 0;
      return state.reverse
        ? clamp(1 - (time / duration), 0, 1)
        : clamp(time / duration, 0, 1);
    };

    const syncVideoCompletion = (state) => {
      const activeProgress = videoProgress(state, state.active);
      const targetProgress = videoProgress(state, state.target);
      state.sequence.classList.toggle("is-complete", activeProgress >= 0.995 && targetProgress >= 0.995);
    };

    const setInitialTime = (state) => {
      const duration = state.video.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;
      const time = state.reverse ? duration : 0;
      state.active = time;
      state.target = time;
      state.ready = true;
      state.video.pause();
      state.video.currentTime = time;
      syncVideoCompletion(state);
    };

    states.forEach((state) => {
      state.video.addEventListener("loadedmetadata", () => {
        setInitialTime(state);
        requestVideoUpdate();
      }, { once: true });
      state.video.load();
      if (state.video.readyState >= 1) setInitialTime(state);
    });

    const animateVideos = () => {
      let needsNext = false;
      states.forEach((state) => {
        if (!state.ready) return;
        const diff = state.target - state.active;
        if (Math.abs(diff) < state.snapStep) {
          state.active = state.target;
        } else {
          state.active += clamp(diff, -state.maxStep, state.maxStep);
          needsNext = true;
        }
        state.video.currentTime = Math.max(0, Math.min(state.video.duration, state.active));
        syncVideoCompletion(state);
        if (Math.abs(state.target - state.active) >= state.snapStep) needsNext = true;
      });
      if (needsNext) {
        window.requestAnimationFrame(animateVideos);
      } else {
        videoAnimating = false;
      }
    };

    const requestVideoAnimation = () => {
      if (videoAnimating) return;
      videoAnimating = true;
      window.requestAnimationFrame(animateVideos);
    };

    const updateVideos = () => {
      videoTicking = false;
      states.forEach((state) => {
        if (!state.ready || !state.sequence.isConnected) return;
        const rect = state.sequence.getBoundingClientRect();
        const scrollable = Math.max(1, rect.height - window.innerHeight);
        const rawProgress = clamp(-rect.top / scrollable, 0, 1);
        const progress = clamp(rawProgress / state.complete, 0, 1);
        state.sequence.style.setProperty("--video-progress", progress.toFixed(4));
        const target = state.reverse
          ? state.video.duration * (1 - progress)
          : state.video.duration * progress;
        if (Math.abs(target - state.target) >= 0.01) {
          state.target = target;
          syncVideoCompletion(state);
          requestVideoAnimation();
        } else {
          syncVideoCompletion(state);
        }
      });
    };

    function requestVideoUpdate() {
      if (videoTicking) return;
      videoTicking = true;
      window.requestAnimationFrame(updateVideos);
    }

    window.addEventListener("scroll", requestVideoUpdate, { passive: true });
    window.addEventListener("resize", requestVideoUpdate);
    requestVideoUpdate();
  }

  // ---- Canvas image-sequence rotation (Apple product-page technique) — [data-fanspin] ----
  // Full-frame webp frames are decoded from a hidden <img> stack and drawn to a sticky
  // <canvas> with "cover" fit as the section scrolls. One frame texture resident at a time.
  const fanSpins = [...root.querySelectorAll("[data-fanspin]")];
  fanSpins.forEach((section) => {
    if (section.dataset.jsInit) return;
    section.dataset.jsInit = "1";
    const canvas = section.querySelector(".fan-hero__canvas");
    const frames = [...section.querySelectorAll("[data-frame]")];
    if (!canvas || frames.length < 2) return;
    const ctx = canvas.getContext("2d");
    const count = frames.length;
    const clampN = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
    let current = -1;

    const sizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
    };

    const drawCover = (img) => {
      if (!img || !img.complete || !img.naturalWidth) return;
      const cw = canvas.width;
      const ch = canvas.height;
      const imgRatio = img.naturalWidth / img.naturalHeight;
      const canvasRatio = cw / ch;
      let dw;
      let dh;
      if (imgRatio > canvasRatio) {
        dh = ch;
        dw = ch * imgRatio;
      } else {
        dw = cw;
        dh = cw / imgRatio;
      }
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    };

    const render = (rawIndex) => {
      const index = clampN(Math.round(rawIndex), 0, count - 1);
      if (index === current && current !== -1) return;
      current = index;
      drawCover(frames[index]);
    };

    const paintFirst = () => {
      sizeCanvas();
      current = -1;
      render(0);
    };
    if (frames[0].complete && frames[0].naturalWidth) paintFirst();
    else frames[0].addEventListener("load", paintFirst, { once: true });

    const redraw = () => {
      sizeCanvas();
      const idx = current;
      current = -1;
      render(idx < 0 ? 0 : idx);
    };

    if (reduceMotion) {
      window.addEventListener("resize", redraw);
      return;
    }

    // Rotation completes when [data-fan-artifact] (the "Prima" artifact-text panel)
    // reaches the top of the viewport, not at the end of the whole section — its
    // offsetTop is read live every frame so this stays correct across resizes and
    // content edits, never a hardcoded scroll distance. Falls back to the old
    // full-section distance if the panel is missing.
    const artifactPanel = section.querySelector("[data-fan-artifact]");
    let fanTicking = false;
    const updateFan = () => {
      fanTicking = false;
      if (!section.isConnected) return; // editor swapped the section out
      const rect = section.getBoundingClientRect();
      const fallback = Math.max(1, rect.height - window.innerHeight);
      const artifactOffset = artifactPanel ? Math.max(1, artifactPanel.offsetTop) : fallback;
      const progress = clampN(-rect.top / artifactOffset, 0, 1);
      section.style.setProperty("--fan-progress", progress.toFixed(4));
      render(progress * (count - 1));
    };
    const requestFan = () => {
      if (fanTicking) return;
      fanTicking = true;
      window.requestAnimationFrame(updateFan);
    };
    window.addEventListener("scroll", requestFan, { passive: true });
    window.addEventListener("resize", () => { redraw(); requestFan(); });

    // Nudge decode of every frame after first paint so scrubbing never hits an undecoded frame.
    const warmDecode = () => {
      frames.forEach((img) => {
        if (typeof img.decode === "function") img.decode().catch(() => {});
      });
    };
    if ("requestIdleCallback" in window) window.requestIdleCallback(warmDecode, { timeout: 1500 });
    else window.setTimeout(warmDecode, 400);

    requestFan();
  });

}

document.addEventListener("DOMContentLoaded", () => {
  initStaticChrome();
  initMobileV2();
});

function updatePurchaseButtonLabel(form) {
  if (!form) return;
  const btn = form.querySelector("[data-add-btn]");
  if (!btn || btn.dataset.loading === "true") return;

  const buyRoot = form.closest("[data-buy]") || document;
  const orientationGroup = buyRoot.querySelector("[data-orientation-group]");
  const isChosen = !orientationGroup || orientationGroup.dataset.chosen === "true";
  const requiredLabel = btn.dataset.requiredLabel;
  const readyLabel = btn.dataset.readyLabel;

  if (!isChosen && requiredLabel) {
    btn.textContent = requiredLabel;
    return;
  }

  if (!readyLabel) return;
  const selectedHand = btn.dataset.selectedHand;
  btn.textContent = form.classList.contains("is-stuck-bottom") && selectedHand
    ? `${selectedHand} · ${readyLabel}`
    : readyLabel;
}

function initProductForm(form) {
  if (!form || form.dataset.jsInit) return;
  form.dataset.jsInit = "1";
  const variantSelect = form.querySelector("[data-variant-id]");
  const optionSelects = [...form.querySelectorAll("[data-option-index]")];
  const status = form.querySelector("[data-form-status]");

  function currentVariantId() {
    if (!optionSelects.length) return variantSelect.value;
    const chosen = optionSelects.map((s) => s.value);
    const opt = [...variantSelect.options].find((o) =>
      chosen.every((v, i) => o.dataset["option" + (i + 1)] === v)
    );
    return opt ? opt.value : variantSelect.value;
  }
  optionSelects.forEach((s) =>
    s.addEventListener("change", () => {
      variantSelect.value = currentVariantId();
    })
  );

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const btn = form.querySelector("[data-add-btn]");
    if (status) status.textContent = "";

    // Orientation gate (mobile PDP round 3): the buy button looks and is
    // operable, but submit is blocked until a hand (L/R) is chosen — the
    // status live region + focus jump do the explaining (initOrientationChips
    // records the choice on the group's data-chosen).
    if (form.hasAttribute("data-require-orientation")) {
      const buyRoot = form.closest("[data-buy]") || document;
      const orientationGroup = buyRoot.querySelector("[data-orientation-group]");
      if (orientationGroup && orientationGroup.dataset.chosen !== "true") {
        updatePurchaseButtonLabel(form);
        // Draw attention to the required control without treating this
        // navigation step as an error. Reflow lets repeated taps re-fire it.
        orientationGroup.classList.remove("needs-orientation");
        void orientationGroup.offsetWidth;
        orientationGroup.classList.add("needs-orientation");
        const firstChip = orientationGroup.querySelector("[data-orientation-chip]");
        if (firstChip) {
          const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
          orientationGroup.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
          firstChip.focus({ preventScroll: true });
        }

        return;
      }
    }

    btn.disabled = true;
    btn.dataset.loading = "true";
    btn.setAttribute("aria-busy", "true");
    btn.textContent = btn.dataset.loadingLabel || "Adding...";

    // Line-item properties (e.g. properties[Orientation] on the theme-only
    // fallback route, when Orientation isn't a real Shopify variant option).
    const properties = {};
    form.querySelectorAll('input[name^="properties["]').forEach((input) => {
      if (!input.value) return;
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = input.value;
    });

    try {
      await Cart.add(Number(currentVariantId()), 1, Object.keys(properties).length ? properties : null);
      const c = await Cart.get();
      setCartCount(c.item_count);
      document.dispatchEvent(new CustomEvent("cart:open"));
    } catch (err) {
      if (status) status.textContent = "We couldn\u2019t add Prima. Check your connection and try again.";
    } finally {
      delete btn.dataset.loading;
      btn.removeAttribute("aria-busy");
      btn.disabled = false;
      updatePurchaseButtonLabel(form);
    }
  });
}
// Colour swatches: swap the media layer, re-tint the cinematic hero (shared
// --cine-tint var on <html>), and — when a product is configured — drive the
// hidden option <select> so initProductForm's variant logic stays the single
// source of truth. Guarded: pages without [data-swatch-group] no-op.
function initSwatches(scope = document) {
  const allChips = document.querySelectorAll("[data-swatch]");
  scope.querySelectorAll("[data-swatch-group]").forEach((group) => {
    if (group.dataset.jsInit) return;
    group.dataset.jsInit = "1";
    const root = group.closest("[data-buy]") || document.querySelector("[data-buy]") || group.parentElement;
    const selects = [...root.querySelectorAll("[data-option-index]")];
    const select = selects.find((s) => (s.dataset.optionName || "").toLowerCase() === "colour" || (s.dataset.optionName || "").toLowerCase() === "color") || selects[0] || null;
    const layers = [...root.querySelectorAll("[data-buy-layer]")];
    const chips = [...group.querySelectorAll("[data-swatch]")];
    
    const setRoving = (activeChip) => {
      chips.forEach((c) => c.setAttribute("tabindex", c === activeChip ? "0" : "-1"));
    };

    const apply = (chip) => {
      const idx = chip.dataset.index;
      const val = chip.dataset.value;
      // Variant select
      if (select && val) {
        if (select.value !== val) {
          select.value = val;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
      // Media layer swap
      layers.forEach((l) => {
        const on = l.dataset.index === idx;
        l.classList.toggle("is-active", on);
        l.setAttribute("aria-hidden", on ? "false" : "true");
      });
      // Hero re-tint
      document.documentElement.style.setProperty("--cine-tint", chip.dataset.color || "transparent");
      
      // Global chip state sync across all groups
      allChips.forEach((c) => {
        if (c.dataset.value === val) {
          c.classList.add("is-active");
          c.setAttribute("aria-checked", "true");
        } else {
          c.classList.remove("is-active");
          c.setAttribute("aria-checked", "false");
        }
      });
      
      // Roving tabindex per group
      document.querySelectorAll("[data-swatch-group]").forEach((g) => {
        const gChips = [...g.querySelectorAll("[data-swatch]")];
        const active = gChips.find((c) => c.dataset.value === val);
        if (active) {
          gChips.forEach((c) => c.setAttribute("tabindex", c === active ? "0" : "-1"));
        }
      });

      const nameEl = root.querySelector("[data-swatch-name]");
      if (nameEl) nameEl.textContent = chip.getAttribute("aria-label") || val || "";
    };

    chips.forEach((chip) => chip.addEventListener("click", () => apply(chip)));
    group.addEventListener("keydown", (e) => {
      const keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"];
      if (!keys.includes(e.key)) return;
      e.preventDefault();
      const cur = chips.findIndex((c) => c.getAttribute("tabindex") === "0");
      const dir = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1;
      const next = (cur + dir + chips.length) % chips.length;
      apply(chips[next]);
      chips[next].focus();
    });
    
    // Initialise hero tint from the active swatch
    const active = chips.find((c) => c.classList.contains("is-active")) || chips[0];
    if (active) document.documentElement.style.setProperty("--cine-tint", active.dataset.color || "transparent");
    if (active) setRoving(active);
  });
}

// Orientation chips (mobile PDP round 3): radio-group semantics with roving
// tabindex, mirroring initSwatches's interaction pattern. Writes the chosen
// hand BOTH ways — into the real variant option select when Orientation is a
// Shopify product option, or into the properties[Orientation] hidden input
// on the theme-only fallback route — then clears the add-to-bag gate set by
// [data-require-orientation] on the form. Guarded: pages without
// [data-orientation-group] no-op.
function initOrientationChips(scope = document) {
  scope.querySelectorAll("[data-orientation-group]").forEach((group) => {
    if (group.dataset.jsInit) return;
    group.dataset.jsInit = "1";
    const chips = [...group.querySelectorAll("[data-orientation-chip]")];
    if (!chips.length) return;
    const root = group.closest("[data-buy]") || document;
    const setRoving = (activeChip) => chips.forEach((c) => c.setAttribute("tabindex", c === activeChip ? "0" : "-1"));

    const apply = (chip) => {
      chips.forEach((c) => {
        const on = c === chip;
        c.classList.toggle("is-active", on);
        c.setAttribute("aria-checked", String(on));
      });
      setRoving(chip);
      group.dataset.chosen = "true";
      group.classList.remove("needs-orientation");

      const purchaseButton = root.querySelector("[data-add-btn]");
      if (purchaseButton) {
        purchaseButton.dataset.selectedHand = chip.textContent.trim();
        updatePurchaseButtonLabel(purchaseButton.closest("form"));
      }
      const status = root.querySelector("[data-form-status]");
      if (status) status.textContent = "";

      const select = [...root.querySelectorAll("[data-option-index]")].find((s) =>
        /orientation/i.test(s.dataset.optionName || "")
      );
      if (select) {
        select.value = chip.dataset.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        const propertyInput = root.querySelector("[data-orientation-property]");
        if (propertyInput) propertyInput.value = chip.dataset.value;
      }
    };

    chips.forEach((chip) => chip.addEventListener("click", () => apply(chip)));
    group.addEventListener("keydown", (e) => {
      const keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"];
      if (!keys.includes(e.key)) return;
      e.preventDefault();
      const cur = chips.findIndex((c) => c.getAttribute("tabindex") === "0");
      const dir = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1;
      const next = (Math.max(cur, 0) + dir + chips.length) % chips.length;
      apply(chips[next]);
      chips[next].focus();
    });
    setRoving(chips[0]);
  });
}
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-product-form]").forEach(initProductForm);
  initReducedMotionVideo();
  initSwatches();
  initOrientationChips();
  initDesktopVideos();
});

function initReducedMotionVideo(root = document) {
  if (!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches)) return;
  root.querySelectorAll(".cine__stage video, .buy__video, .details__video").forEach((v) => { try { v.pause(); } catch (e) {} v.removeAttribute("autoplay"); });
}

// Desktop-only hero/manifesto videos ([data-desktop-video]) ship with
// preload="none" and no <source src> — the markup only carries a data-src —
// so mobile (where the section is display:none via mobile-v2.css) never
// downloads the .mp4. On desktop widths we swap the source's real src in,
// call load(), then play(). If the page loads below the breakpoint, a single
// matchMedia change listener loads the video the moment a resize crosses it
// (e.g. rotating a tablet, or a desktop window dragged narrow then wide).
// Reduced-motion users get the video loaded (so it's not stuck on a poster
// forever) but never autoplaying, matching initReducedMotionVideo's
// pause + remove-autoplay behavior for other video classes.
function initDesktopVideos(root = document) {
  const videos = [...root.querySelectorAll("video[data-desktop-video]")].filter((video) => {
    if (video.dataset.jsInit) return false;
    video.dataset.jsInit = "1";
    return true;
  });
  if (!videos.length || !(window.matchMedia)) return;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const desktopQuery = window.matchMedia("(min-width: 761px)");

  const loadVideo = (video) => {
    if (video.dataset.loaded === "true") return;
    video.dataset.loaded = "true";
    const source = video.querySelector("source[data-src]");
    if (source) source.src = source.dataset.src;
    video.load();
    if (reduceMotion) {
      video.pause();
      video.removeAttribute("autoplay");
    } else {
      video.play().catch(() => {});
    }
  };

  if (desktopQuery.matches) {
    videos.forEach(loadVideo);
    return;
  }

  const onDesktopChange = (e) => {
    if (!e.matches) return;
    videos.forEach(loadVideo);
    if (desktopQuery.removeEventListener) desktopQuery.removeEventListener("change", onDesktopChange);
    else if (desktopQuery.removeListener) desktopQuery.removeListener(onDesktopChange);
  };
  if (desktopQuery.addEventListener) desktopQuery.addEventListener("change", onDesktopChange);
  else if (desktopQuery.addListener) desktopQuery.addListener(onDesktopChange);
}

function initCartDrawer() {
  const drawer = document.querySelector("[data-cart-drawer]");
  if (!drawer) return;
  const body = drawer.querySelector("[data-cart-body]");
  const panel = drawer.querySelector("[data-cart-panel]") || drawer;
  const subtotalRow = drawer.querySelector("[data-cart-subtotal-row]");
  const subtotalVal = drawer.querySelector("[data-cart-subtotal]");
  const statusEl = drawer.querySelector("[data-cart-status]");
  const footEl = drawer.querySelector(".drawer__foot");
  let lastFocused = null;

  // In-flight guard — only one mutation at a time.
  let changing = false;

  const cell = (text, cls) => {
    const s = document.createElement("span");
    if (cls) s.className = cls;
    s.textContent = text ?? "";
    return s;
  };

  // Shared close-button lookup: prefer the real focusable close control
  // (.drawer__close) and only fall back to [data-cart-close] for markup
  // variants where the scrim also carries that attribute (it isn't
  // reliably focusable).
  const focusCloseButton = () => {
    const closeBtn = drawer.querySelector(".drawer__close") || drawer.querySelector("[data-cart-close]");
    if (closeBtn) closeBtn.focus();
  };

  function setCartError(msg) {
    if (statusEl) statusEl.textContent = msg;
  }

  function clearCartError() {
    if (statusEl) statusEl.textContent = "";
  }

  function updateTotals(cart) {
    const hasItems = cart.items.length > 0;
    if (subtotalVal) subtotalVal.textContent = Money(cart.total_price);
    if (subtotalRow) subtotalRow.hidden = !hasItems;
    if (footEl) footEl.hidden = !hasItems;
  }

  function renderItems(cart) {
    body.replaceChildren();
    clearCartError();
    updateTotals(cart);
    if (!cart.items.length) {
      const emptyText = body.dataset.emptyText || "Your cart is empty";
      body.appendChild(cell(emptyText));
      if (body.dataset.emptyCtaUrl) {
        const emptyCta = document.createElement("a");
        emptyCta.className = "drawer__empty-cta btn";
        emptyCta.href = body.dataset.emptyCtaUrl;
        emptyCta.textContent = body.dataset.emptyCtaLabel || "Discover";
        body.appendChild(emptyCta);
      }
      // Move focus to close so it is not stranded after the last item is removed.
      focusCloseButton();
      return;
    }
    // Build rows with textContent (no innerHTML) to avoid XSS from cart data.
    cart.items.forEach((item) => {
      const row = document.createElement("div");
      row.className = "drawer__row";

      // Title
      const title = document.createElement("p");
      title.className = "drawer__row-title";
      title.textContent = item.product_title;

      // Variant title (if not default)
      if (item.variant_title && item.variant_title !== "Default Title") {
        const variantEl = document.createElement("span");
        variantEl.className = "drawer__row-variant label";
        variantEl.textContent = item.variant_title;
        title.appendChild(document.createElement("br"));
        title.appendChild(variantEl);
      }

      // Line-item properties (e.g. properties[Orientation] on the mobile
      // PDP's mandatory hand choice) — reuses the existing muted variant
      // style so the customer's choice stays visible in the drawer, not
      // just at checkout. "_"-prefixed keys are internal/hidden by
      // Shopify convention and skipped, as are empty values.
      if (item.properties) {
        Object.keys(item.properties).forEach((key) => {
          if (key.indexOf("_") === 0) return;
          const value = item.properties[key];
          if (value === null || value === undefined || value === "") return;
          const propEl = document.createElement("span");
          propEl.className = "drawer__row-variant label drawer__item-prop";
          propEl.textContent = key + ": " + value;
          title.appendChild(document.createElement("br"));
          title.appendChild(propEl);
        });
      }

      // Line price
      const price = document.createElement("p");
      price.className = "drawer__row-price label";
      price.textContent = Money(item.final_line_price);

      // Quantity stepper
      const stepper = document.createElement("div");
      stepper.className = "drawer__stepper";

      const btnMinus = document.createElement("button");
      btnMinus.type = "button";
      btnMinus.className = "drawer__stepper-btn";
      btnMinus.setAttribute("aria-label", "Decrease quantity of " + item.product_title);
      btnMinus.textContent = "−";
      // Disable minus when quantity is 1 (Remove button handles full removal).
      const initQty = parseInt(item.quantity, 10);
      btnMinus.disabled = !isNaN(initQty) && initQty <= 1;

      const qtyDisplay = document.createElement("span");
      qtyDisplay.className = "drawer__stepper-val label";
      qtyDisplay.textContent = String(!isNaN(initQty) ? initQty : item.quantity);

      const btnPlus = document.createElement("button");
      btnPlus.type = "button";
      btnPlus.className = "drawer__stepper-btn";
      btnPlus.setAttribute("aria-label", "Increase quantity of " + item.product_title);
      btnPlus.textContent = "+";

      stepper.append(btnMinus, qtyDisplay, btnPlus);

      // Remove button
      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "drawer__remove label";
      removeBtn.setAttribute("aria-label", "Remove " + item.product_title);
      removeBtn.textContent = "Remove";

      // applyChange uses item.key (stable line-item key) not a positional index.
      const applyChange = async (quantity) => {
        if (changing) return;
        changing = true;
        // Disable ALL controls in the drawer body during the request.
        body.querySelectorAll("button").forEach((b) => { b.disabled = true; });
        clearCartError();
        try {
          await Cart.change(item.key, quantity);
          const updated = await Cart.get();
          setCartCount(updated.item_count);
          renderItems(updated);
          // After a successful re-render move focus to the close button
          // so focus is not stranded on a removed node.
          focusCloseButton();
        } catch (err) {
          setCartError(err.message || "Could not update cart");
          // Re-enable controls manually (no re-render on error).
          body.querySelectorAll("button").forEach((b) => { b.disabled = false; });
          // Restore minus disabled state based on displayed quantity.
          const displayed = parseInt(qtyDisplay.textContent, 10);
          if (!isNaN(displayed) && displayed <= 1) btnMinus.disabled = true;
        } finally {
          changing = false;
        }
      };

      btnMinus.addEventListener("click", () => {
        const displayed = parseInt(qtyDisplay.textContent, 10);
        const current = isNaN(displayed) ? 1 : displayed;
        applyChange(Math.max(1, current - 1));
      });
      btnPlus.addEventListener("click", () => {
        const displayed = parseInt(qtyDisplay.textContent, 10);
        const current = isNaN(displayed) ? 1 : displayed;
        applyChange(current + 1);
      });
      removeBtn.addEventListener("click", () => applyChange(0));

      row.append(title, price, stepper, removeBtn);
      body.appendChild(row);
    });
  }

  const open = async () => {
    lastFocused = document.activeElement;
    body.replaceChildren(cell("Loading…"));
    if (subtotalRow) subtotalRow.hidden = true;
    if (footEl) footEl.hidden = true;
    drawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    focusCloseButton();
    try {
      const c = await Cart.get();
      renderItems(c);
    } catch (err) {
      body.replaceChildren(cell(err.message || "Could not load cart"));
    }
  };

  const close = () => {
    drawer.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
  };

  const isOpen = () => drawer.getAttribute("aria-hidden") === "false";

  // Focus trap: intercept EVERY Tab while the drawer is open, not only at boundaries.
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Tab" || !isOpen()) return;
    const focusable = [...panel.querySelectorAll(
      'a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])'
    )];
    if (!focusable.length) { e.preventDefault(); return; }
    if (focusable.length === 1) { e.preventDefault(); focusable[0].focus(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const inPanel = focusable.includes(active);
    if (e.shiftKey) {
      if (!inPanel || active === first) { e.preventDefault(); last.focus(); }
    } else {
      if (!inPanel || active === last) { e.preventDefault(); first.focus(); }
    }
  });

  document.addEventListener("cart:open", open);
  document.querySelectorAll("[data-cart-open]").forEach((el) => {
    el.addEventListener("click", () => document.dispatchEvent(new CustomEvent("cart:open")));
  });
  drawer.querySelectorAll("[data-cart-close]").forEach((el) => el.addEventListener("click", close));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isOpen()) close();
  });
  // Clear the scroll lock if the page is left while the drawer is open.
  window.addEventListener("pagehide", () => {
    document.body.style.overflow = "";
  });
}
document.addEventListener("DOMContentLoaded", initCartDrawer);

function initNav() {
  const toggle = document.querySelector("[data-nav-toggle]");
  const nav = document.getElementById("PrimaryNav");
  if (!toggle || !nav) return;

  const closeNav = () => {
    toggle.setAttribute("aria-expanded", "false");
    nav.classList.remove("site-header__nav--open");
    toggle.focus();
  };

  toggle.addEventListener("click", () => {
    const open = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", String(!open));
    nav.classList.toggle("site-header__nav--open", !open);
    if (!open) {
      const firstLink = nav.querySelector("a");
      if (firstLink) firstLink.focus();
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && toggle.getAttribute("aria-expanded") === "true") {
      closeNav();
    }
  });
}
document.addEventListener("DOMContentLoaded", initNav);

// Mobile Sticky Buy Form
function initMobileStickyBuy(root = document) {
  const form = root.querySelector(".mobile-product-form");
  if (!form || form.dataset.stickyInit || !form.parentNode) return;
  form.dataset.stickyInit = "1";

  const sentinel = document.createElement("div");
  sentinel.setAttribute("aria-hidden", "true");
  sentinel.style.height = "0px";
  form.parentNode.insertBefore(sentinel, form);

  const spacer = document.createElement("div");
  spacer.setAttribute("aria-hidden", "true");
  spacer.style.height = "0px";
  form.parentNode.insertBefore(spacer, form.nextSibling);

  let naturalHeight = 0;
  let isStuck = false;
  const page = document.documentElement;

  const syncPageOffset = () => {
    if (!isStuck) return;
    page.style.setProperty("--mobile-sticky-buy-offset", `${Math.ceil(form.getBoundingClientRect().height)}px`);
  };

  const resizeObserver = "ResizeObserver" in window ? new ResizeObserver(syncPageOffset) : null;
  resizeObserver?.observe(form);

  const applyState = (nextState) => {
    if (nextState === isStuck) return;
    isStuck = nextState;

    if (isStuck) {
      if (!naturalHeight) naturalHeight = form.offsetHeight;
      spacer.style.height = naturalHeight + "px";
      form.classList.add("is-stuck-bottom");
      page.classList.add("has-mobile-sticky-buy");
      requestAnimationFrame(syncPageOffset);
    } else {
      naturalHeight = form.offsetHeight;
      spacer.style.height = "0px";
      form.classList.remove("is-stuck-bottom");
      page.classList.remove("has-mobile-sticky-buy");
      page.style.removeProperty("--mobile-sticky-buy-offset");
    }

    updatePurchaseButtonLabel(form);
  };

  let ticking = false;
  const update = () => {
    ticking = false;

    if (!form.isConnected) {
      resizeObserver?.disconnect();
      page.classList.remove("has-mobile-sticky-buy");
      page.style.removeProperty("--mobile-sticky-buy-offset");
      return;
    }

    if (window.innerWidth > 760) {
      applyState(false);
      return;
    }

    const sentRect = sentinel.getBoundingClientRect();
    const nh = naturalHeight || form.offsetHeight || 60;
    const bottomThreshold = window.innerHeight - nh;
    const formBottom = sentRect.top + nh;
    const belowViewport = isStuck
      ? sentRect.top > bottomThreshold - 12
      : sentRect.top > bottomThreshold + 12;
    const aboveViewport = sentinel.offsetParent !== null && (isStuck ? formBottom < 12 : formBottom < -12);

    applyState(belowViewport || aboveViewport);
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  window.addEventListener("scroll", requestUpdate, { passive: true });
  window.addEventListener("resize", requestUpdate);
  update();
}
document.addEventListener("DOMContentLoaded", () => initMobileStickyBuy());
// Two "join" blocks exist (mobile flow #join-mobile, desktop flow #join), each hidden
// at the other viewport — #join links must land on whichever one is actually visible.
function initJoinAnchor() {
  const visibleJoin = () =>
    [document.getElementById("join"), document.getElementById("join-mobile")].find(
      (el) => el && el.getClientRects().length > 0
    );
  const scrollToJoin = () => {
    const target = visibleJoin();
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  if (window.location.hash === "#join") {
    requestAnimationFrame(scrollToJoin);
  }
  document.addEventListener("click", (e) => {
    const link = e.target.closest && e.target.closest('a[href$="#join"]');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname) return;
    e.preventDefault();
    scrollToJoin();
    if (history.replaceState) history.replaceState(null, "", "#join");
  });
}
document.addEventListener("DOMContentLoaded", initJoinAnchor);

// Decorative "scroll to continue" cues (e.g. the fan-hero chevron) fade out for
// good once the visitor starts scrolling — never reappear, even back at the top.
// Guarded: pages without [data-scroll-cue] no-op.
function initScrollCue(root = document) {
  const cues = [...root.querySelectorAll("[data-scroll-cue]")].filter((cue) => {
    if (cue.dataset.jsInit) return false;
    cue.dataset.jsInit = "1";
    return true;
  });
  if (!cues.length) return;
  let dismissed = false;
  const checkScroll = () => {
    if (dismissed || window.scrollY <= 40) return;
    dismissed = true;
    cues.forEach((cue) => cue.classList.add("is-hidden"));
    window.removeEventListener("scroll", checkScroll);
  };
  window.addEventListener("scroll", checkScroll, { passive: true });
  checkScroll();
}
document.addEventListener("DOMContentLoaded", () => initScrollCue());

// ---- Theme Editor (design mode) support ----
// The editor re-injects a section's HTML on every setting change WITHOUT a
// page reload (shopify:section:load) — nothing bound on DOMContentLoaded runs
// again, which used to leave edited sections dead: .reveal copy invisible
// ("the section in front disappeared", owner 2026-07-08), sliders/product
// form/canvas rotation unbound. Re-run the section-scoped initialisers for
// the reloaded fragment; every init above carries a data-js-init guard so
// re-entry never double-binds an element. Listener is registered
// unconditionally — the event only ever fires inside the Theme Editor.
document.addEventListener("shopify:section:load", (event) => {
  const sectionRoot = event.target;
  if (!sectionRoot || sectionRoot.nodeType !== 1) return;
  initReveals(sectionRoot);
  initMobileV2(sectionRoot);
  sectionRoot.querySelectorAll("[data-product-form]").forEach(initProductForm);
  initSwatches(sectionRoot);
  initOrientationChips(sectionRoot);
  initReducedMotionVideo(sectionRoot);
  initDesktopVideos(sectionRoot);
  initMobileStickyBuy(sectionRoot);
  initScrollCue(sectionRoot);
});
